"use client";

import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { QuantitySlider } from "@/app/components/ui/quantity-slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { Key, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/app/components/ui/copy-button";

export default function PasswordGeneratorTool() {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState(12);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeSimilar, setExcludeSimilar] = useState(false);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);

  const generatePassword = () => {
    let charset = "";
    
    if (includeUppercase) charset += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (includeLowercase) charset += "abcdefghijklmnopqrstuvwxyz";
    if (includeNumbers) charset += "0123456789";
    if (includeSymbols) charset += "!@#$%^&*()_+-=[]{}|;:,.<>?";

    if (excludeSimilar) {
      charset = charset.replace(/[il1Lo0O]/g, "");
    }

    if (excludeAmbiguous) {
      charset = charset.replace(/[{}[\]()\/\\'\"`~,;.<>]/g, "");
    }

    if (charset.length === 0) {
      toast.error("Please select at least one character type");
      return;
    }

    let newPassword = "";
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    setPassword(newPassword);
    toast.success("Password generated successfully!");
  };

  const copyToClipboard = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    toast.success("Password copied to clipboard!");
  };

  const clearAll = () => {
    setPassword("");
    toast.info("Password cleared");
  };

  const getPasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    
    if (score <= 2) return { level: "Weak", color: "text-red-500" };
    if (score <= 4) return { level: "Medium", color: "text-yellow-500" };
    return { level: "Strong", color: "text-green-500" };
  };

  const strength = getPasswordStrength(password);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Password Generator
            </CardTitle>
            <CardDescription>
              Generate secure passwords with customizable options
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <QuantitySlider
                label="Password Length"
                value={length}
                min={4}
                max={50}
                step={1}
                onValueChange={setLength}
                hint="4–50 characters"
              />

              <div className="space-y-3">
                <Label>Character Types</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="uppercase"
                      checked={includeUppercase}
                      onCheckedChange={(checked) => setIncludeUppercase(checked === true)}
                    />
                    <Label htmlFor="uppercase">Uppercase letters (A-Z)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="lowercase"
                      checked={includeLowercase}
                      onCheckedChange={(checked) => setIncludeLowercase(checked === true)}
                    />
                    <Label htmlFor="lowercase">Lowercase letters (a-z)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="numbers"
                      checked={includeNumbers}
                      onCheckedChange={(checked) => setIncludeNumbers(checked === true)}
                    />
                    <Label htmlFor="numbers">Numbers (0-9)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="symbols"
                      checked={includeSymbols}
                      onCheckedChange={(checked) => setIncludeSymbols(checked === true)}
                    />
                    <Label htmlFor="symbols">Symbols (!@#$%^&*)</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Exclusions</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="excludeSimilar"
                      checked={excludeSimilar}
                      onCheckedChange={(checked) => setExcludeSimilar(checked === true)}
                    />
                    <Label htmlFor="excludeSimilar">Exclude similar characters (il1Lo0O)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="excludeAmbiguous"
                      checked={excludeAmbiguous}
                      onCheckedChange={(checked) => setExcludeAmbiguous(checked === true)}
                    />
                    <Label htmlFor="excludeAmbiguous">Exclude ambiguous characters</Label>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={generatePassword}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Generate Password
                </Button>
                <Button onClick={clearAll} variant="outline">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Generated Password</CardTitle>
            <CardDescription>
              Your secure password will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                value={password}
                readOnly
                className="text-lg [font-family:var(--font-jetbrains-mono)]"
                placeholder="Generated password will appear here..."
              />
            </div>

            {password && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Strength</Label>
                  <span className={`font-semibold ${strength.color}`}>
                    {strength.level}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>Length: {password.length}</span>
                    <span>Characters: {new Set(password).size}</span>
                  </div>
                </div>
              </div>
            )}

            {password && (
              <CopyButton
                text={password}
                variant="default"
                size="default"
                className="w-full"
                successMessage="Password copied!"
              >
                Copy Password
              </CopyButton>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Password Security Tips</CardTitle>
          <CardDescription>
            Best practices for secure passwords
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold text-green-600">✅ Do</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Use at least 12 characters</li>
                <li>• Include uppercase, lowercase, numbers, and symbols</li>
                <li>• Use unique passwords for each account</li>
                <li>• Consider using a password manager</li>
                <li>• Change passwords regularly</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold text-red-600">❌ Don't</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Use personal information</li>
                <li>• Use common words or patterns</li>
                <li>• Reuse passwords across accounts</li>
                <li>• Share passwords with others</li>
                <li>• Store passwords in plain text</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
